import { useState, useCallback } from "react";
import { api1 } from "../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../services/apiEndpoints";
import { getAllMainstreamCareers, getAllEmergingCareers } from "../../../services/dashboardService";
import { emptyCat } from "../constants";
import useLightcastToken from "../../../utils/useLightcastToken";

export const useSkillsGapData = (occupationId, currentJob) => {
  const { lightCastToken } = useLightcastToken();
  const [skillsCategories, setSkillsCategories] = useState(emptyCat);

  const mapToCategories = useCallback((data = {}) => {
    setSkillsCategories(Object.fromEntries(
      Object.entries({
        commonSkills: { title: "Common Skills*", description: "Soft skills common across jobs." },
        definingSkills: { title: "Defining Skills*", description: "Specialized skills for core tasks." },
        distinguishingSkills: { title: "Distinguishing Skills*", description: "Advanced but less frequent." },
        salaryBoostingSkills: { title: "Salary Boosting Skills*", description: "High-value skills that boost pay." }
      }).map(([k, v]) => [k, { ...v, skills: (data[k] || []).map(s => (s?.name ?? s)) }])
    ));
    return true;
  }, []);

  const clearCategoriesWithError = useCallback((msg = "No skills found for this occupation.") => {
    setSkillsCategories(emptyCat());
    return msg;
  }, []);

  const fetchTargetOccupationSkills = useCallback(async (title, type) => {
    if (!title) return { success: false, error: "No title provided" };
    
    try {
      const endpoint = type === "emerging"
        ? API_ENDPOINTS.EMERGING_SKILLS_BY_OCCUPATION
        : API_ENDPOINTS.MAINSTREAM_SKILLS_BY_OCCUPATION;
      
      const requestBody = type === "emerging" 
        ? { occupationTitle: title }
        : { occupationTitle: title, lightcastToken: lightCastToken };
      
      const { data } = await api1.post(endpoint, requestBody);
      const skillsData = data?.data || data?.skills;
      
      if (skillsData) {
        mapToCategories(skillsData);
        return { success: true, data: skillsData };
      } else {
        const error = clearCategoriesWithError();
        return { success: false, error };
      }
    } catch (error) {
      const errorMsg = clearCategoriesWithError();
      return { success: false, error: errorMsg };
    }
  }, [mapToCategories, clearCategoriesWithError, lightCastToken]);

  const loadTargetCareers = useCallback(async () => {
    try {
      const [mainstreamResponse, emergingResponse] = await Promise.all([
        getAllMainstreamCareers(),
        getAllEmergingCareers()
      ]);
      
      const mainstreamCareers = mainstreamResponse?.data?.careers || [];
      const emergingCareers = emergingResponse?.data?.careers || [];

      const processedMainstream = mainstreamCareers.map(career => ({
        id: career,
        name: career,
        value: career,
        label: career,
        type: "mainstream"
      }));
      
      const processedEmerging = emergingCareers.map(career => ({
        id: career,
        name: career,
        value: career,
        label: career,
        type: "emerging"
      }));
      
      const allCareers = [...processedMainstream, ...processedEmerging];
      
      return { success: true, data: allCareers };
    } catch (error) {
      return { success: false, error: "Failed to load target careers" };
    }
  }, []);

  return {
    skillsCategories,
    setSkillsCategories,
    fetchTargetOccupationSkills,
    loadTargetCareers
  };
};
